import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Frown, Eye, EyeOff, Edit, Trash2, MoreHorizontal } from "lucide-react";
import { DataTable } from "../components/common/DataTable";
import CreateCategoryModal from "../components/category/CreateCategoryModal";
import UpdateCategoryModal from "../components/category/UpdateCategoryModal";
import { fetchCategories, deleteCategory } from "../service/categoryService";
import { S3_BASE_URL } from "../utils/Constants";

export default function Category() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    document.documentElement.setAttribute(
      "dir",
      i18n.language === "ar" ? "rtl" : "ltr"
    );
  }, [i18n.language]);

  const {
    data: list = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const deleteCat = useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => qc.invalidateQueries(["categories"]),
  });

  // Filter categories based on status
  const filteredList = list.filter((category) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "visible") return category.isListed;
    if (statusFilter === "hidden") return !category.isListed;
    return true;
  });

  const handleDelete = (category) => {
    if (window.confirm(t("deleteConfirmSingle", { name: category.name }))) {
      deleteCat.mutate(category.id);
    }
  };

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  if (isError)
    return (
      <div className="alert alert-error shadow-lg m-6">
        <span>{error.message}</span>
      </div>
    );

  const columns = [
    { key: "index", header: "#", isIndex: true, render: (_r, i) => i + 1 },
    {
      key: "name",
      header: t("name"),
      render: (r) => (
        <div className="flex items-center space-x-2">
          <span>{r.name}</span>
          {r.imageUrl && (
            <img
              src={S3_BASE_URL + "/" + r.imageUrl}
              alt={r.name}
              className="w-8 h-8 rounded object-cover"
            />
          )}
        </div>
      ),
    },
    {
      key: "description",
      header: t("description"),
      render: (r) => (
        <span className="text-sm text-gray-600 truncate max-w-xs">
          {r.description || "-"}
        </span>
      ),
    },
    {
      key: "status",
      header: t("status.title"),
      render: (r) => (
        <div className="flex items-center space-x-2">
          {r.isListed ? (
            <>
              <Eye size={16} className="text-green-500" />
              <span className="text-green-600 text-sm font-medium">
                {t("visible")}
              </span>
            </>
          ) : (
            <>
              <EyeOff size={16} className="text-gray-500" />
              <span className="text-gray-500 text-sm font-medium">
                {t("hidden")}
              </span>
            </>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      header: t("actions"),
      render: (r) => (
        <div className="flex items-center space-x-1">
          <button
            className="flex items-center space-x-1 px-2 py-1 rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-800 transition-colors duration-200"
            onClick={() => setEditingCategory(r)}
            title={t("edit")}
          >
            <Edit size={14} />
            <span className="text-xs font-medium">{t("edit")}</span>
          </button>
          <button
            className="flex items-center space-x-1 px-2 py-1 rounded-md text-red-600 hover:bg-red-50 hover:text-red-800 transition-colors duration-200"
            onClick={() => handleDelete(r)}
            disabled={deleteCat.isLoading}
            title={t("delete")}
          >
            <Trash2 size={14} />
            <span className="text-xs font-medium">
              {deleteCat.isLoading ? t("deleting") : t("delete")}
            </span>
          </button>
        </div>
      ),
    },
  ];

  const stats = {
    total: list.length,
    visible: list.filter((c) => c.isListed).length,
    hidden: list.filter((c) => !c.isListed).length,
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t("categories")}
          </h1>
          <p className="text-gray-600 mt-1">{t("manageFoodCategories")}</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          {t("createCategory")}
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">{t("totalCategories")}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-blue-100 p-2 rounded-full">
              <span className="text-blue-600 text-lg">📂</span>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">{t("visibleCategories")}</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.visible}
              </p>
            </div>
            <div className="bg-green-100 p-2 rounded-full">
              <Eye className="text-green-600" size={20} />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">{t("hiddenCategories")}</p>
              <p className="text-2xl font-bold text-gray-500">{stats.hidden}</p>
            </div>
            <div className="bg-gray-100 p-2 rounded-full">
              <EyeOff className="text-gray-500" size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">
            {t("filterByStatus")}:
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => setStatusFilter("all")}
              className={`px-3 py-1 rounded-full text-sm ${
                statusFilter === "all"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {t("all")} ({stats.total})
            </button>
            <button
              onClick={() => setStatusFilter("visible")}
              className={`px-3 py-1 rounded-full text-sm ${
                statusFilter === "visible"
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {t("visible")} ({stats.visible})
            </button>
            <button
              onClick={() => setStatusFilter("hidden")}
              className={`px-3 py-1 rounded-full text-sm ${
                statusFilter === "hidden"
                  ? "bg-gray-100 text-gray-700"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {t("hidden")} ({stats.hidden})
            </button>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow border">
        <DataTable
          data={filteredList}
          columns={columns}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          emptyState={
            <div className="flex-1 flex flex-col items-center justify-center py-16">
              <Frown size={80} className="text-gray-300" />
              <p className="mt-4 text-xl text-gray-500">
                {statusFilter === "all"
                  ? t("noCategories")
                  : t("noCategoriesForFilter")}
              </p>
              {statusFilter !== "all" && (
                <button
                  onClick={() => setStatusFilter("all")}
                  className="mt-2 text-blue-600 hover:text-blue-800"
                >
                  {t("viewAllCategories")}
                </button>
              )}
            </div>
          }
        />
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateCategoryModal onClose={() => setShowCreateModal(false)} />
      )}
      {editingCategory && (
        <UpdateCategoryModal
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
        />
      )}
    </div>
  );
}
